import React, { useState, useEffect } from 'react';
import { Modal } from '@components/common';
import { sendInvoice } from '@lib/api/invoices';
import type { InvoiceDetail } from '@lib/api/invoices';

interface SendInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceDetail;
  /** Called after a successful send so the parent can refresh invoice state / list. */
  onSuccess: (emailed: boolean) => void;
  /** When true, shows "Resend" UI instead of "Send" */
  isResend?: boolean;
}

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const SendInvoiceModal: React.FC<SendInvoiceModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onSuccess,
  isResend = false,
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ emailed: boolean; to?: string; reason?: string } | null>(null);
  const [error, setError] = useState('');

  // Reset state when the modal opens
  useEffect(() => {
    if (isOpen) {
      setMessage('');
      setResult(null);
      setError('');
      setSending(false);
    }
  }, [isOpen]);

  const hasEmail = Boolean(invoice.client_email);
  const balance = invoice.balance_due != null ? invoice.balance_due : invoice.total;

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await sendInvoice(invoice.id, message.trim() || undefined);
      setResult({ emailed: res.emailed, to: res.emailTo, reason: res.emailReason });
      onSuccess(res.emailed);
    } catch {
      setError('Failed to send invoice. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isResend ? 'Resend Invoice to Client' : 'Send Invoice to Client'}
      size="md"
      footer={
        result ? (
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-5 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={sending}
              className="px-5 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !hasEmail}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              title={!hasEmail ? 'No email address on record for this client' : undefined}
            >
              {sending ? (
                <>
                  <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                  Sending…
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-base">send</span>
                  {isResend ? 'Resend to Client' : 'Send to Client'}
                </>
              )}
            </button>
          </div>
        )
      }
    >
      <div className="space-y-5">
        {/* Result banner */}
        {result && (
          <div
            className={`flex items-start gap-3 rounded-lg p-4 border ${
              result.emailed
                ? 'bg-green-500/10 border-green-500/30 text-green-300'
                : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
            }`}
          >
            <span className="material-symbols-outlined text-xl mt-0.5">
              {result.emailed ? 'check_circle' : 'warning'}
            </span>
            <div>
              <p className="font-semibold text-sm">
                {result.emailed
                  ? 'Invoice emailed successfully'
                  : 'Invoice marked as sent — email not delivered'}
              </p>
              {result.emailed && result.to && (
                <p className="text-xs mt-1 opacity-80">Sent to {result.to} · Copy saved to Sent folder</p>
              )}
              {!result.emailed && result.reason && (
                <p className="text-xs mt-1 opacity-80">{result.reason}</p>
              )}
            </div>
          </div>
        )}

        {/* Invoice summary */}
        {!result && (
          <>
            <div className="bg-[#101922] rounded-lg border border-gray-700 p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Invoice</span>
                <span className="text-white font-semibold text-sm">{invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Client</span>
                <span className="text-white text-sm">
                  {invoice.client_company || invoice.client_name || '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Balance Due</span>
                <span className="text-amber-400 font-semibold text-sm">{money(balance)}</span>
              </div>
            </div>

            {/* Recipient email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                To (Client Email)
              </label>
              {hasEmail ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#101922] border border-gray-700 text-gray-300 text-sm">
                  <span className="material-symbols-outlined text-base text-gray-500">mail</span>
                  {invoice.client_email}
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  <span className="material-symbols-outlined text-base">warning</span>
                  No email address on record for this client. Update the client profile to enable emailing.
                </div>
              )}
            </div>

            {/* Optional message */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Message to Client{' '}
                <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Please find your invoice ${invoice.invoice_number} attached. Thank you for your business.`}
                rows={4}
                className="w-full px-3 py-2 rounded-lg bg-[#101922] border border-gray-700 text-white placeholder-gray-600 text-sm resize-none focus:outline-none focus:border-blue-500 transition-colors"
                disabled={sending}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use the default invoice email template.
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                <span className="material-symbols-outlined text-base">error</span>
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
